import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Select, Slider, Spin, Switch } from "antd";

import { ContentDrawer } from "./styles";
import { useAuth } from "../../context/auth";
import { useSearch } from "../../context/search";
import { maskArea, maskBRL } from "../../utils/mask";
import { AutocompleteSelect, AutocompleteOption } from "../AutocompleteSelect";
import { QTY_OPTIONS, SearchFilters } from "../../types/searchFilters";

export type { ScrollProps } from "../Header";

const selectPopupProps = {
  listHeight: 240,
  virtual: false as const,
  getPopupContainer: () => document.body,
  popupMatchSelectWidth: true,
};

type QtyField = "qtd_rooms" | "qtd_bathrooms" | "qtd_suite" | "qtd_parking";

export function AdvancedSearch() {
  const { api, categorys, typeBuild } = useAuth();
  const { filters, updateFilter, setFilters } = useSearch();
  const { Option } = Select;

  const [empreendimentos, setEmpreendimentos] = useState<AutocompleteOption[]>(
    [],
  );
  const [construtoras, setConstrutoras] = useState<
    { label: string; value: string }[]
  >([]);
  const [loadingEmpreendimentos, setLoadingEmpreendimentos] = useState(false);

  const idsPorGrupo = useMemo(() => {
    const map: Record<string, number[]> = {};
    typeBuild.forEach((tipo) => {
      const nomeGrupo = tipo.grupo?.trim() || "Empreendimento";
      if (!map[nomeGrupo]) map[nomeGrupo] = [];
      map[nomeGrupo].push(tipo.id);
    });
    return map;
  }, [typeBuild]);

  const toggleOperation = useCallback(
    (key: "is_launch" | "is_sale" | "is_rent") => {
      updateFilter(key, filters[key] === "1" ? "" : "1");
    },
    [filters, updateFilter],
  );

  const toggleQty = useCallback(
    (field: QtyField, option: string) => {
      setFilters((prev) => {
        const current = prev[field];
        const next = current.includes(option)
          ? current.filter((item) => item !== option)
          : [...current, option];
        return { ...prev, [field]: next };
      });
    },
    [setFilters],
  );

  const clearQty = useCallback(
    (field: QtyField) => {
      updateFilter(field, []);
    },
    [updateFilter],
  );

  const fetchEmpreendimentos = useCallback(
    async (query: string) => {
      setLoadingEmpreendimentos(true);
      try {
        const res = await api.get("/portal/building/list-empreendimentos", {
          params: { portal: "hub", query: query || "" },
        });
        const list = (res.data ?? []).map((item: any) => ({
          label:
            item.nome_interno || item.descricao || item.nome || String(item.id),
          value: String(item.id_imovel_kurole ?? item.id),
        }));
        setEmpreendimentos(list);
        return list;
      } catch (e) {
        console.error(e);
        return [];
      } finally {
        setLoadingEmpreendimentos(false);
      }
    },
    [api],
  );

  useEffect(() => {
    fetchEmpreendimentos("");
    api
      .get("/portal/constructors", { params: { portal: "hub" } })
      .then((res) => {
        setConstrutoras(
          (res.data ?? []).map((c: { id: number; nome: string }) => ({
            label: c.nome,
            value: String(c.id),
          })),
        );
      })
      .catch((e) => console.error(e));
  }, [api, fetchEmpreendimentos]);

  const fetchCidades = useCallback(
    async (query: string): Promise<AutocompleteOption[]> => {
      const res = await api.get("/utils/autocomplete-cidade", {
        params: { portal: "hub", query: query || "" },
      });
      return (res.data ?? []).map((c: string) => ({ label: c, value: c }));
    },
    [api],
  );

  const fetchBairros = useCallback(
    async (query: string): Promise<AutocompleteOption[]> => {
      const res = await api.get("/utils/autocomplete-bairro", {
        params: {
          portal: "hub",
          query: query || "",
          cidade: filters.address_city.join(","),
        },
      });
      return (res.data ?? []).map((c: string) => ({ label: c, value: c }));
    },
    [api, filters.address_city],
  );

  const fetchLogradouros = useCallback(
    async (query: string): Promise<AutocompleteOption[]> => {
      const res = await api.get("/utils/autocomplete-logradouro", {
        params: {
          portal: "hub",
          query: query || "",
          cidade: filters.address_city.join(","),
          bairro: filters.address_district.join(","),
        },
      });
      return (res.data ?? []).map((c: string) => ({ label: c, value: c }));
    },
    [api, filters.address_city, filters.address_district],
  );

  const fetchZonas = useCallback(
    async (query: string): Promise<AutocompleteOption[]> => {
      if (!filters.address_city.length) return [];
      const res = await api.get("/utils/autocomplete-zona", {
        params: {
          portal: "hub",
          query: query || "",
          cidade: filters.address_city.join(","),
        },
      });
      return (res.data ?? []).map((c: string) => ({ label: c, value: c }));
    },
    [api, filters.address_city],
  );

  const handleMoney =
    (key: keyof SearchFilters) => (event: ChangeEvent<HTMLInputElement>) => {
      const inputValue = Number(event.target.value.replace(/\D/g, "")) / 100;
      updateFilter(key as any, inputValue);
    };

  const RoomBullets = ({
    label,
    value,
    field,
  }: {
    label: string;
    value: string[];
    field: QtyField;
  }) => (
    <div className="room-single">
      <span className="field-label">{label}</span>
      <div className="bullets">
        {QTY_OPTIONS.map((n) => (
          <button
            type="button"
            key={n}
            className={`bullet-single ${value.includes(n) ? "clicked" : ""}`}
            onClick={() => toggleQty(field, n)}
          >
            {n}
          </button>
        ))}
      </div>
      <button
        type="button"
        className={`large-bullet-single ${value.length === 0 ? "clicked" : ""}`}
        onClick={() => clearQty(field)}
      >
        Tanto Faz
      </button>
    </div>
  );

  return (
    <ContentDrawer>
      <div className="field-block">
        <div className="operation-toggles">
          <button
            type="button"
            className={`operation-chip ${filters.is_launch === "1" ? "active" : ""}`}
            onClick={() => toggleOperation("is_launch")}
          >
            Lançamento
          </button>
          <button
            type="button"
            className={`operation-chip ${filters.is_sale === "1" ? "active" : ""}`}
            onClick={() => toggleOperation("is_sale")}
          >
            Venda
          </button>
          <button
            type="button"
            className={`operation-chip ${filters.is_rent === "1" ? "active" : ""}`}
            onClick={() => toggleOperation("is_rent")}
          >
            Aluguel
          </button>
        </div>
      </div>

      <div className="field-block">
        <span className="field-label">Categorias</span>
        <Select
          allowClear
          placeholder="Selecione uma categoria"
          value={filters.id_category || undefined}
          onChange={(val) =>
            updateFilter("id_category", val ? String(val) : "")
          }
          className="filter-select"
          options={categorys?.map((cat) => ({
            value: String(cat.id),
            label: cat.titulo,
          }))}
          {...selectPopupProps}
        />
      </div>

      <div className="field-block">
        <span className="field-label">Empreendimento</span>
        <Select
          showSearch
          allowClear
          placeholder="Selecione um empreendimento"
          value={filters.id_empreendimento || undefined}
          onChange={(val) =>
            updateFilter("id_empreendimento", val ? String(val) : "")
          }
          onSearch={(q) => fetchEmpreendimentos(q)}
          filterOption={false}
          loading={loadingEmpreendimentos}
          className="filter-select"
          notFoundContent={
            loadingEmpreendimentos ? <Spin size="small" /> : "Nenhum resultado"
          }
          {...selectPopupProps}
        >
          {empreendimentos.map((item) => (
            <Option key={item.value} value={item.value}>
              {item.label}
            </Option>
          ))}
        </Select>
      </div>

      <div className="field-block">
        <span className="field-label">Construtora</span>
        <Select
          showSearch
          allowClear
          placeholder="Selecione uma construtora"
          value={filters.id_constructor || undefined}
          onChange={(val) =>
            updateFilter("id_constructor", val ? String(val) : "")
          }
          optionFilterProp="label"
          className="filter-select"
          options={construtoras}
          {...selectPopupProps}
        />
      </div>

      <div className="field-block">
        <span className="field-label">Cidade</span>
        <AutocompleteSelect
          mode="multiple"
          value={filters.address_city}
          placeholder="Selecione a(s) cidade(s)"
          fetchOptions={fetchCidades}
          onChange={(val) => {
            const cities = Array.isArray(val) ? val : val ? [val] : [];
            setFilters((prev) => ({
              ...prev,
              address_city: cities,
              address_district: [],
              address_street: "",
              address_zone: [],
            }));
          }}
        />
      </div>

      <div className="field-block">
        <span className="field-label">Bairro</span>
        <AutocompleteSelect
          mode="multiple"
          value={filters.address_district}
          placeholder="Selecione o(s) bairro(s)"
          fetchOptions={fetchBairros}
          dependencies={[filters.address_city.join(",")]}
          onChange={(val) => {
            const districts = Array.isArray(val) ? val : val ? [val] : [];
            setFilters((prev) => ({
              ...prev,
              address_district: districts,
              address_street: "",
            }));
          }}
        />
      </div>

      <div className="field-block">
        <span className="field-label">Logradouro</span>
        <AutocompleteSelect
          value={filters.address_street}
          placeholder="Selecione o logradouro"
          fetchOptions={fetchLogradouros}
          dependencies={[
            filters.address_city.join(","),
            filters.address_district.join(","),
          ]}
          onChange={(val) =>
            updateFilter(
              "address_street",
              Array.isArray(val) ? val[0] || "" : val,
            )
          }
        />
      </div>

      <div className="field-block">
        <span className="field-label">Zona</span>
        <AutocompleteSelect
          mode="multiple"
          value={filters.address_zone}
          placeholder={
            filters.address_city.length
              ? "Selecione a(s) zona(s)"
              : "Selecione uma cidade primeiro"
          }
          disabled={!filters.address_city.length}
          fetchOptions={fetchZonas}
          dependencies={[filters.address_city.join(",")]}
          onChange={(val) => {
            const zones = Array.isArray(val) ? val : val ? [val] : [];
            updateFilter("address_zone", zones);
          }}
        />
      </div>

      <p className="section-title">Valor do imóvel</p>
      <p className="sub-label">Venda</p>
      <div className="range">
        <input
          value={maskBRL(filters.min_sale_price)}
          onChange={handleMoney("min_sale_price")}
        />
        <Slider
          className="range-slider"
          range
          max={3000000}
          step={10000}
          tooltip={{ formatter: (value) => `${maskBRL(value)}` }}
          value={[filters.min_sale_price, filters.max_sale_price]}
          onChange={(e) => {
            setFilters((prev) => ({
              ...prev,
              min_sale_price: e[0],
              max_sale_price: e[1],
            }));
          }}
        />
        <input
          value={maskBRL(filters.max_sale_price)}
          onChange={handleMoney("max_sale_price")}
        />
      </div>

      <p className="sub-label">Aluguel</p>
      <div className="range">
        <input
          value={maskBRL(filters.min_rent_price)}
          onChange={handleMoney("min_rent_price")}
        />
        <Slider
          className="range-slider"
          range
          max={10000}
          tooltip={{ formatter: (value) => `${maskBRL(value)}` }}
          value={[filters.min_rent_price, filters.max_rent_price]}
          onChange={(e) => {
            setFilters((prev) => ({
              ...prev,
              min_rent_price: e[0],
              max_rent_price: e[1],
            }));
          }}
        />
        <input
          value={maskBRL(filters.max_rent_price)}
          onChange={handleMoney("max_rent_price")}
        />
      </div>

      <div className="field-block">
        <span className="field-label">Condomínio + IPTU</span>
        <div className="range">
          <input
            value={maskBRL(filters.min_taxas)}
            onChange={handleMoney("min_taxas")}
          />
          <Slider
            className="range-slider"
            range
            max={1000000}
            tooltip={{ formatter: (value) => `${maskBRL(value)}` }}
            value={[filters.min_taxas, filters.max_taxas]}
            onChange={(e) => {
              setFilters((prev) => ({
                ...prev,
                min_taxas: e[0],
                max_taxas: e[1],
              }));
            }}
          />
          <input
            value={maskBRL(filters.max_taxas)}
            onChange={handleMoney("max_taxas")}
          />
        </div>
      </div>

      <div className="field-block">
        <span className="field-label">Tipo de Imóvel</span>
        <div className="types-switch-list">
          {Object.entries(idsPorGrupo)
            .filter(
              ([grupo]) => grupo.trim().toLowerCase() !== "empreendimento",
            )
            .map(([grupo, ids]) => {
              const checked = ids.every((id) =>
                filters.id_type.includes(String(id)),
              );
              return (
                <label key={grupo} className="type-switch-row">
                  <Switch
                    size="small"
                    checked={checked}
                    onChange={(isChecked) => {
                      setFilters((prev) => {
                        if (isChecked) {
                          return {
                            ...prev,
                            id_type: Array.from(
                              new Set([...prev.id_type, ...ids.map(String)]),
                            ),
                          };
                        }
                        return {
                          ...prev,
                          id_type: prev.id_type.filter(
                            (id) => !ids.map(String).includes(id),
                          ),
                        };
                      });
                    }}
                    style={{
                      backgroundColor: checked ? "#0e2244" : undefined,
                    }}
                  />
                  <span>{grupo.charAt(0).toUpperCase() + grupo.slice(1)}</span>
                </label>
              );
            })}
        </div>
      </div>

      <div className="field-block">
        <span className="field-label">Área</span>
        <div className="range">
          <input
            value={maskArea(filters.min_area.toString())}
            onChange={(e) =>
              updateFilter(
                "min_area",
                Number(e.target.value.replace(/\D/g, "")) || 0,
              )
            }
          />
          <Slider
            className="range-slider"
            range
            max={10000}
            tooltip={{ formatter: (value) => `${value} m²` }}
            value={[filters.min_area, filters.max_area]}
            onChange={(e) => {
              setFilters((prev) => ({
                ...prev,
                min_area: e[0],
                max_area: e[1],
              }));
            }}
          />
          <input
            value={maskArea(filters.max_area.toString())}
            onChange={(e) =>
              updateFilter(
                "max_area",
                Number(e.target.value.replace(/\D/g, "")) || 0,
              )
            }
          />
        </div>
      </div>

      <RoomBullets
        label="Nº de quartos"
        value={filters.qtd_rooms}
        field="qtd_rooms"
      />
      <RoomBullets
        label="Nº de banheiros"
        value={filters.qtd_bathrooms}
        field="qtd_bathrooms"
      />
      <RoomBullets
        label="Nº de suítes"
        value={filters.qtd_suite}
        field="qtd_suite"
      />
      <RoomBullets
        label="Nº de vagas"
        value={filters.qtd_parking}
        field="qtd_parking"
      />

      <div className="field-block">
        <span className="field-label">Mobiliado</span>
        <div className="operation-toggles">
          <button
            type="button"
            className={`operation-chip ${filters.is_mobiliado === "" ? "active" : ""}`}
            onClick={() => updateFilter("is_mobiliado", "")}
          >
            Ambos
          </button>
          <button
            type="button"
            className={`operation-chip ${filters.is_mobiliado === "1" ? "active" : ""}`}
            onClick={() => updateFilter("is_mobiliado", "1")}
          >
            Sim
          </button>
          <button
            type="button"
            className={`operation-chip ${filters.is_mobiliado === "0" ? "active" : ""}`}
            onClick={() => updateFilter("is_mobiliado", "0")}
          >
            Não
          </button>
        </div>
      </div>
    </ContentDrawer>
  );
}
